import React, { useMemo, useState } from 'react';

import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/24px/icon_24px_search.svg';
import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/24px/icon_24px_location.svg';

import {
  applyResponsiveTypography,
  BaseButton,
  JobBoardLocationAutoCompleteList,
  useT,
} from '@repo/common';

const responsiveTypo = (mobileKey, desktopKey, value) =>
  applyResponsiveTypography(mobileKey, desktopKey, value);

/**
 * 서치바 입력 행.
 *
 * 상태
 * - default : 배경 흰색, placeholder gray, 아이콘 placeholder
 * - focused : green-light03 링 + 그림자로 떠오름. 링은 레이아웃을 밀지 않아 카드 크기가 유지된다.
 * - filled  : 포커스가 풀려도 값이 있으면 텍스트/아이콘이 primary로 남는다
 */
const SearchField = ({
  icon: Icon,
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  isFocused,
  roundedClass = '',
  children,
}) => {
  const isFilled = Boolean(value);
  const isActive = isFocused || isFilled;

  return (
    <div className='relative w-full tabletV:h-full tabletV:min-w-px tabletV:flex-[1_0_0]'>
      <div
        className={`
          relative flex h-[54px] w-full items-center gap-3 bg-grayscale-gray00 pl-3 pr-2 py-2
          transition-[box-shadow] duration-200
          tabletV:h-full tabletV:gap-4 tabletV:px-[30px]
          ${roundedClass}
          ${
            isFocused
              ? 'z-10 shadow-2 ring-[1.5px] ring-green-light03 tabletV:ring-grayscale-gray09'
              : 'ring-0'
          }
        `}
      >
        <Icon
          className={`size-5 tabletH:size-6 shrink-0 transition-colors ${
            isActive ? 'text-icon-primary' : 'text-icon-placeholder'
          }`}
        />

        <input
          type='text'
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={`
            min-w-0 flex-[1_0_0] bg-transparent text-text-primary outline-none
            placeholder:text-text-placeholder
            ${responsiveTypo('b16', 'b18', value)}
          `}
        />
      </div>

      {children}
    </div>
  );
};

const Divider = ({ hidden }) => (
  <div
    aria-hidden='true'
    className={`h-px w-full shrink-0 transition-colors tabletV:hidden ${
      hidden ? 'bg-transparent' : 'bg-grayscale-gray02'
    }`}
  />
);

const SearchBar = ({ onSearch, jobs = [] }) => {
  const t = useT();
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('');
  const [includeRemote, setIncludeRemote] = useState(false);

  const [isJobFocused, setIsJobFocused] = useState(false);
  const [isLocationFocused, setIsLocationFocused] = useState(false);

  const jobSuggestions = useMemo(() => {
    const keyword = jobTitle.trim().toLowerCase();
    if (keyword.length < 2) return [];

    const unique = new Set();
    const suggestions = [];

    jobs.forEach((job) => {
      [job?.title, job?.company].forEach((candidate) => {
        const value = String(candidate || '').trim();
        if (!value) return;
        if (!value.toLowerCase().includes(keyword)) return;
        if (unique.has(value.toLowerCase())) return;

        unique.add(value.toLowerCase());
        suggestions.push(value);
      });
    });

    return suggestions.slice(0, 6);
  }, [jobTitle, jobs]);

  const handleSearch = () => {
    onSearch?.({
      jobTitle: jobTitle.trim(),
      location: location.trim(),
    });
  };

  const handleLocationSelect = (item) => {
    const nextLocation = `${item.name}${
      item.country ? `, ${item.country}` : ''
    }`;
    setLocation(nextLocation);
    setIsLocationFocused(false);
  };

  const handleJobSuggestionSelect = (value) => {
    setJobTitle(value);
    setIsJobFocused(false);
  };

  const handleToggleRemote = () => {
    setIncludeRemote((prev) => {
      const next = !prev;

      setLocation((prevLocation) => {
        if (next) {
          if (prevLocation.toLowerCase().includes('remote'))
            return prevLocation;
          return prevLocation ? `${prevLocation}, Remote` : 'Remote';
        }

        return prevLocation
          .replace(/,\s*remote/gi, '')
          .replace(/^remote,\s*/gi, '')
          .replace(/^remote$/gi, '')
          .trim();
      });

      return next;
    });
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div
      className='
        relative flex w-full flex-col overflow-visible rounded-[28px]
        bg-grayscale-gray00 shadow-3
        tabletV:h-20 tabletV:flex-row tabletV:items-center tabletV:rounded-[80px]
      '
    >
      <div className='hidden shrink-0 tabletV:block tabletV:h-full tabletV:w-px' />

      <div className='relative flex w-full flex-col tabletV:h-full tabletV:min-h-px tabletV:min-w-px tabletV:flex-[1_0_0] tabletV:flex-row tabletV:items-center tabletV:gap-px tabletV:py-0 tabletV:pr-2'>
        {/* 직무 / 키워드 */}
        <SearchField
          icon={SearchIcon}
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          onFocus={() => setIsJobFocused(true)}
          onBlur={() => {
            setTimeout(() => setIsJobFocused(false), 120);
          }}
          onKeyDown={handleEnter}
          placeholder={t(
            'Job title, keyword or company',
            '직무명, 키워드 또는 회사명',
          )}
          isFocused={isJobFocused}
          roundedClass='rounded-t-[28px] tabletV:rounded-none tabletV:rounded-bl-[100px] tabletV:rounded-tl-[100px]'
        >
          {/* 자동완성은 absolute로 띄워 카드 높이를 유지한다 */}
          {isJobFocused && jobSuggestions.length > 0 && (
            <div
              className='absolute left-0 top-full z-20 mt-1 max-h-[50vh] w-full overflow-y-auto overscroll-contain rounded-lg border border-grayscale-gray02 bg-grayscale-gray00 p-2 shadow-popup-02'
              onMouseDown={(e) => e.preventDefault()}
            >
              {jobSuggestions.map((suggestion) => (
                <BaseButton
                  key={suggestion}
                  type='text'
                  size='m'
                  hideIcon
                  typo={false}
                  isStatic
                  leftalign
                  className='h-auto w-full min-w-0 cursor-pointer whitespace-normal break-words border-0 bg-transparent px-3 py-2 text-left rounded-lg typo-b14 text-text-primary hover:bg-grayscale-gray01'
                  textWrapperClass='h-auto'
                  onClick={() => handleJobSuggestionSelect(suggestion)}
                >
                  {suggestion}
                </BaseButton>
              ))}
            </div>
          )}
        </SearchField>

        <Divider hidden={isJobFocused || isLocationFocused} />

        {/* 지역 */}
        <SearchField
          icon={LocationIcon}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          onFocus={() => setIsLocationFocused(true)}
          onBlur={() => {
            setTimeout(() => setIsLocationFocused(false), 150);
          }}
          onKeyDown={handleEnter}
          placeholder={t('City, area or remote', '도시, 지역 또는 원격 근무')}
          isFocused={isLocationFocused}
        >
          {isLocationFocused && location.length >= 2 && (
            <div
              className='absolute left-0 top-full z-20 mt-1 w-full'
              onMouseDown={(e) => e.preventDefault()}
            >
              <JobBoardLocationAutoCompleteList
                searchText={location}
                onSelect={handleLocationSelect}
                includeRemote={includeRemote}
                onToggleRemote={handleToggleRemote}
              />
            </div>
          )}
        </SearchField>
      </div>

      <Divider hidden={isLocationFocused} />

      <div className='relative flex h-auto w-full shrink-0 items-center p-2 tabletV:h-full tabletV:w-auto tabletV:p-0 tabletV:pr-3'>
        <BaseButton
          type='primary'
          size='xl'
          hideIcon
          typo={false}
          onClick={handleSearch}
          textWrapperClass='h-auto flex-none'
          className='relative flex h-[60px] w-full min-w-0 shrink-0 items-center justify-center rounded-full border-0 bg-green-deep02 transition-colors hover:bg-green-deep04 active:bg-green-deep05 tabletV:w-[140px]'
        >
          <p
            className={`${responsiveTypo(
              'b18Strong',
              'b18Strong',
            )} relative shrink-0 text-text-inverse`}
          >
            {t('Search', '검색')}
          </p>
        </BaseButton>
      </div>
    </div>
  );
};

export default SearchBar;
